import React, {
    useCallback, useState, useRef, useEffect,
} from 'react';
import classNames from 'classnames';
import { numeralFormatterCurrency } from 'ns_libs/formatter';
import ReadOnlyWrapper from 'components/ReadOnlyWrapper/ReadOnlyWrapper';
import { Tooltip } from 'reactstrap';
import { generatePath, useNavigate } from 'react-router-dom';
import useAuthContext from 'contexts/AuthContext';
import { URLS } from 'services/urls';
import { customStyles } from 'bricks/NSSelect/NSSelect';
import { DEVELOPMENT_SOURCE_METRIC } from 'constants/sources';
import { mockData } from 'views/ProFormaTable/constants';
import { isDebtSource } from 'utils/sourceTypeUtils';
import { useGetProFormaSources, useGetProFormaSensitivityAnalysis } from 'views/ProFormaTable/hooks';
import useCreateProFormaFromSensitivity from '../../../../queries/ProForma/useCreateProFormaFromSensitivity';
import { NSButton, NSCard, NSSelect } from '../../../../bricks';
import RenderIf from '../../../../components/RenderIf/RenderIf';
import { SensitivityAnalysisParameter, SensitivityAnalysisParams, SensitivityAnalysisResponse } from '../../types';
import { SensitivityValue } from '../SensitivityTable/types';
import SensitivityTable from '../SensitivityTable/SensitivityTable';
import ProFormaExitValuationCard from '../ProFormaExitValuationCard/ProFormaExitValuationCard';
import { useProFormaSocketContext } from '../ProForma/socketContext/ProFormaSocketProvider';
import {
    EMPTY_DATA_VALUES, getSensitivityConfig, getSensitivityOptions, getGrossRentLabel,
    GROSS_RENT_LABEL, INTEREST_RATE_VS_LTV, getSensitivityIdFromType, HORIZONTAL, VERTICAL,
} from './constants';
import { useSensitivityStorage } from './hooks/useSensitivityStorage';

interface TableConfig {
    id: string;
    type: string;
    tableLabel: string;
}

interface SensitivityTableContainerProps {
    dealId: number;
    proFormaId: number;
    tableConfig: TableConfig;
    onRequestDelete: (tableId: string) => void;
    proFormaInProgress?: boolean;
}

const DEFAULT_VERTICAL_INCREMENT = 1;
const DEFAULT_HORIZONTAL_INCREMENT = 1;

const SensitivityTableContainer = ({
    dealId,
    proFormaId,
    tableConfig,
    onRequestDelete,
    proFormaInProgress = false,
}: SensitivityTableContainerProps) => {
    const [selectedCell, setSelectedCell] = useState<{ cell: SensitivityValue, row: number, col: number } | null>(null);
    const [showTooltip, setShowTooltip] = useState(false);
    const navigate = useNavigate();
    const { mutate: createProFormaFromSensitivity } = useCreateProFormaFromSensitivity();
    const { selectedOrganizationId } = useAuthContext();
    const [selectedSourceId, setSelectedSourceId] = useState<number | null>(null);
    const { proForma, showCents, sourceTypes } = useProFormaSocketContext();
    const { saveSensitivity, getIncrements } = useSensitivityStorage(proFormaId.toString(), proForma?.proFormaModelId);

    const selectedOption = getSensitivityOptions(proForma?.proFormaModelId).find(opt => opt.value === tableConfig.type);
    const sensitivityId = getSensitivityIdFromType(tableConfig.type) || '1';
    const storedIncrements = getIncrements(sensitivityId, proForma?.proFormaModelId);
    const increments = {
        vertical: storedIncrements.vertical,
        horizontal: storedIncrements.horizontal,
    };

    const defaultIncrements = {
        vertical: selectedOption?.increments?.vertical || DEFAULT_VERTICAL_INCREMENT,
        horizontal: selectedOption?.increments?.horizontal || DEFAULT_HORIZONTAL_INCREMENT,
    };

    const currentParamsRef = useRef<SensitivityAnalysisParams>({
        parameters: selectedOption?.value as SensitivityAnalysisParameter,
        firstParameterStepMultiplier: increments.vertical,
        secondParameterStepMultiplier: increments.horizontal,
    });

    useEffect(() => {
        currentParamsRef.current = {
            parameters: selectedOption?.value as SensitivityAnalysisParameter,
            firstParameterStepMultiplier: increments.vertical,
            secondParameterStepMultiplier: increments.horizontal,
        };
    }, [selectedOption?.value, increments.vertical, increments.horizontal]);

    const isLoanToCostScenario = selectedOption?.horizontalLabel === 'LTC';
    const {
        data,
        isLoading,
        isFetching,
        refetch,
    } = useGetProFormaSensitivityAnalysis(
        dealId,
        proFormaId,
        currentParamsRef.current,
        selectedSourceId,
        { enabled: !proFormaInProgress && !!currentParamsRef.current && (!isLoanToCostScenario || selectedSourceId !== null) },
    );

    const transformSensitivityData = useCallback((apiData: SensitivityAnalysisResponse | undefined) => {
        if (!apiData) return null;

        const verticalLabels = [...apiData.sensitivityAnalysis].reverse().map(item => ({
            id: `vertical-${item.firstParameterValue}`,
            label: item.firstParameterValue,
        }));

        const horizontalLabels = apiData.sensitivityAnalysis[0].bySecond.map(item => ({
            id: `horizontal-${item.secondParameterValue}`,
            label: item.secondParameterValue || undefined,
        }));

        const reverseValues = [...apiData.sensitivityAnalysis].reverse();
        const values = reverseValues.flatMap(firstParam => firstParam.bySecond.map(secondParam => ({
            exitValuation: secondParam.exitValuation ? parseFloat(secondParam.exitValuation) : undefined,
            netOperatingIncome: secondParam.netOperatingIncome ? parseFloat(secondParam.netOperatingIncome) : undefined,
            constructionDebtAmount: secondParam.constructionDebtAmount ? parseFloat(secondParam.constructionDebtAmount) : undefined,
            leveredIRR: secondParam.leveredIrr ? parseFloat(secondParam.leveredIrr) : undefined,
            leveredEMx: secondParam.leveredEmx ? parseFloat(secondParam.leveredEmx) : undefined,
            stabilizedYieldOnCost: secondParam.stabilizedYieldOnCost ? parseFloat(secondParam.stabilizedYieldOnCost) : undefined,
            equityProceeds: secondParam.equityProceeds ? parseFloat(secondParam.equityProceeds) : undefined,
            status: secondParam.status,
        })));

        let verticalLabel = apiData.firstParameter;
        if (verticalLabel.toLowerCase().includes(GROSS_RENT_LABEL)) {
            verticalLabel = getGrossRentLabel(proForma?.proFormaModelId);
        }

        return {
            values,
            verticalLabel,
            horizontalLabel: apiData.secondParameter,
            verticalDataLabels: verticalLabels,
            horizontalDataLabels: horizontalLabels,
        };
    }, [proForma?.proFormaModelId]);

    const handleLabelChange = async (direction: typeof VERTICAL | typeof HORIZONTAL, change: number) => {
        try {
            const current = currentParamsRef?.current;
            const minValue = Math.abs(change);

            const newVerticalMultiplier = direction === VERTICAL
                ? Math.max(minValue, current?.firstParameterStepMultiplier + change)
                : current?.firstParameterStepMultiplier;
            const newHorizontalMultiplier = direction === HORIZONTAL
                ? Math.max(minValue, current?.secondParameterStepMultiplier + change)
                : current.secondParameterStepMultiplier;

            currentParamsRef.current = {
                ...current,
                firstParameterStepMultiplier: newVerticalMultiplier,
                secondParameterStepMultiplier: newHorizontalMultiplier,
            };

            saveSensitivity(sensitivityId, newVerticalMultiplier, newHorizontalMultiplier);

            await refetch();
        } catch (error) {
            console.error('Failed to fetch sensitivity data:', error);
        }
    };

    const handleCellClick = (cellData: SensitivityValue | null, row?: number, col?: number) => {
        if (cellData && row !== undefined && col !== undefined) {
            setSelectedCell({ cell: cellData, row, col });
        } else {
            setSelectedCell(null);
        }
    };

    const tableData = transformSensitivityData(data) || {
        values: [],
        verticalLabel: mockData.verticalLabel,
        horizontalLabel: mockData.horizontalLabel,
        verticalDataLabels: [],
        horizontalDataLabels: [],
    };

    const cardVisible = Boolean(selectedCell);
    const sensitivityConfig = getSensitivityConfig(showCents);
    const config = sensitivityConfig[selectedOption?.label as keyof typeof sensitivityConfig] || {
        displayValue: 'exitValuation',
        formatter: (value: number | string | undefined) => numeralFormatterCurrency(value, showCents ? '$0,0.00' : '$0,0'),
    };

    let disableVerticalDecrement = false;
    let disableHorizontalDecrement = false;

    if (data?.sensitivityAnalysis.length) {
        const selectedOption = getSensitivityOptions(proForma?.proFormaModelId).find(opt => opt.value === tableConfig.type);
        const verticalStep = selectedOption?.increments?.vertical ?? 0.001;
        const horizontalStep = selectedOption?.increments?.horizontal ?? 0.001;

        const firstParamDifference = Number(data.sensitivityAnalysis[4].firstParameterValue)
            - Number(data.sensitivityAnalysis[0].firstParameterValue);

        disableVerticalDecrement = Math.abs(firstParamDifference - (verticalStep * 4)) < 1e-5;

        const secondParamDifference = Number(data.sensitivityAnalysis[0].bySecond[4].secondParameterValue)
            - Number(data.sensitivityAnalysis[0].bySecond[0].secondParameterValue);
        disableHorizontalDecrement = Math.abs(secondParamDifference - (horizontalStep * 4)) < 1e-5;
    }

    const handleCreateProForma = () => {
        if (!selectedCell || !tableData.verticalDataLabels || !tableData.horizontalDataLabels) return;

        const parameters: Record<string, string> = {};
        const selectedOption = getSensitivityOptions(proForma?.proFormaModelId).find(opt => opt.value === tableConfig.type);

        if (selectedOption) {
            const parameterMap: Record<string, string> = {
                'Exit cap rate': 'exit_cap_rate',
                'Hard + soft costs': 'hard_soft_costs',
                'Gross rent': 'gross_rent',
                'Total rental income': 'gross_rent',
                'Vacancy rate': 'vacancy_rate',
                'Lease-up pace': 'lease_up_pace',
                'Operating expenses': 'operating_expenses',
                'Interest rate': 'interest_rate',
                'Acquisition price': 'acquisition_price',
                LTC: 'ltc',
            };

            const verticalValue = tableData.verticalDataLabels[selectedCell.row]?.label ?? '';
            const horizontalValue = tableData.horizontalDataLabels[selectedCell.col]?.label ?? '';
            parameters[parameterMap[selectedOption.verticalLabel]] = verticalValue;
            parameters[parameterMap[selectedOption.horizontalLabel]] = horizontalValue;
            if (isLoanToCostScenario && selectedSourceId) parameters.source_id = selectedSourceId.toString();
        }

        createProFormaFromSensitivity(
            {
                dealId, proFormaId, parameters, organizationId: selectedOrganizationId!!,
            },
            {
                onSuccess: newProForma => {
                    const path = generatePath(URLS.PRO_FORMA.TABLE, { dealId, proFormaId: newProForma.id });
                    navigate(path);
                },
            },
        );
    };

    const { data: proFormaSourcesData = [] } = useGetProFormaSources(dealId, proFormaId, {
        enabled: isLoanToCostScenario && !proFormaInProgress && !!currentParamsRef.current,
    });
    const debtTypeSources = proFormaSourcesData.filter(source => source.sourceTypeId && isDebtSource(source.sourceTypeId, sourceTypes));
    const debtSourceOptions = debtTypeSources.map(source => ({ label: source.name, value: source.id }));
    const debtSourceMetricType = debtTypeSources.find(source => source.metricType !== null)?.metricType || DEVELOPMENT_SOURCE_METRIC.LTV;

    useEffect(() => {
        if (debtTypeSources.length) {
            const highestAmount = Math.max(...debtTypeSources.map(option => option.amount || 0));
            const optionWithHighestAmount = debtTypeSources.find(option => Number(option.amount || 0) === highestAmount);
            setSelectedSourceId(optionWithHighestAmount!.id);
        }
    }, [debtTypeSources.length]);

    const getTableLabel = () => {
        if (isLoanToCostScenario && debtSourceMetricType === DEVELOPMENT_SOURCE_METRIC.LTV) return INTEREST_RATE_VS_LTV;
        if (!selectedOption) return tableConfig.tableLabel;
        const { label } = selectedOption;
        if (label.toLowerCase().includes(GROSS_RENT_LABEL)) {
            return label.replace('Gross rent', getGrossRentLabel(proForma?.proFormaModelId));
        }
        return label;
    };

    return (
        <RenderIf isLoading={isLoading || isFetching} isTrue={data !== undefined || proFormaInProgress}>
            <NSCard className="mb-2 p-2">
                <div className={classNames('Sensitivity__table pr-2', { 'Sensitivity__table--shrinked': cardVisible })}>
                    <div className="d-flex flex-row justify-content-between align-items-center">
                        <div>
                            <div className="Sensitivity__table--title text-white mb-0">{getTableLabel()}</div>
                            <div className="text-muted">Click on a cell to see the impact on returns.</div>
                        </div>
                        <div className="d-flex flex-row align-items-center">
                            <RenderIf isTrue={isLoanToCostScenario}>
                                <div>
                                    <div className="text-dark">Debt source</div>
                                    <NSSelect
                                        name="proFormaDebtSources"
                                        options={debtSourceOptions || []}
                                        value={debtSourceOptions.find(option => option.value === selectedSourceId) || null}
                                        onChange={option => setSelectedSourceId(option.value)}
                                        styles={{
                                            ...customStyles,
                                            control: styles => ({
                                                ...styles,
                                                border: 'none',
                                                backgroundColor: '#47515d',
                                                width: '350px',
                                            }),
                                        }}
                                        placeholder="Select a debt source"
                                        isClearable={false}
                                    />
                                </div>
                            </RenderIf>
                            {!cardVisible && !proFormaInProgress && (
                                <NSButton
                                    className="m-2"
                                    outline
                                    color="danger"
                                    callback={() => onRequestDelete(tableConfig.id)}
                                    disabled={proFormaInProgress}
                                >
                                    Remove sensitivity
                                </NSButton>
                            )}
                        </div>
                    </div>
                    <div className="Sensitivity--separator w-100 pt-2 mb-2" />
                    <div>
                        {proFormaInProgress
                            ? (
                                <ReadOnlyWrapper isReadOnly>
                                    <SensitivityTable
                                        verticalLabel="Hard + Soft Costs"
                                        horizontalLabel="Exit Cap Rate"
                                        verticalDataLabels={Array(5).fill(null).map((_, index) => ({ id: `vertical-${index}` }))}
                                        horizontalDataLabels={Array(5).fill(null).map((_, index) => ({ id: `horizontal-${index}` }))}
                                        data={EMPTY_DATA_VALUES}
                                        disableVerticalDecrement
                                        disableHorizontalDecrement
                                        showDecimal={false}
                                        displayValue="netOperatingIncome"
                                        valueFormatter={(value: string | number): string => value.toString()}
                                        isProFormaInProgress
                                    />
                                </ReadOnlyWrapper>
                            ) : (
                                <SensitivityTable
                                    verticalLabel={tableData.verticalLabel}
                                    horizontalLabel={tableData.horizontalLabel}
                                    verticalDataLabels={tableData.verticalDataLabels}
                                    horizontalDataLabels={tableData.horizontalDataLabels}
                                    data={tableData.values as SensitivityValue[]}
                                    onVerticalIncrement={() => handleLabelChange('vertical', defaultIncrements.vertical)}
                                    onVerticalDecrement={() => handleLabelChange('vertical', -defaultIncrements.vertical)}
                                    onHorizontalIncrement={() => handleLabelChange('horizontal', defaultIncrements.horizontal)}
                                    onHorizontalDecrement={() => handleLabelChange('horizontal', -defaultIncrements.horizontal)}
                                    onCellClick={handleCellClick}
                                    disableVerticalDecrement={disableVerticalDecrement}
                                    disableHorizontalDecrement={disableHorizontalDecrement}
                                    showDecimal={increments.vertical === 0.1 || increments.horizontal === 0.1}
                                    displayValue={config.displayValue}
                                    valueFormatter={config.formatter}
                                />
                            )}
                    </div>
                </div>

                <div
                    className={classNames('Sensitivity__valuation-card-container m-2', {
                        'Sensitivity__valuation-card-container--hidden': !cardVisible,
                    })}
                >
                    <ProFormaExitValuationCard
                        exitValuation={selectedCell?.cell.exitValuation || 0}
                        leveredIRR={selectedCell?.cell.leveredIRR || 0}
                        leveredEMx={selectedCell?.cell.leveredEMx || 0}
                        stabilizedYieldOnCost={selectedCell?.cell.stabilizedYieldOnCost || 0}
                        equityProceeds={selectedCell?.cell.equityProceeds || 0}
                        status={selectedCell?.cell.status}
                        id={tableConfig.id}
                        onCreateProForma={handleCreateProForma}
                    />
                    <Tooltip
                        target="create-pro-forma-button"
                        isOpen={showTooltip}
                        toggle={() => setShowTooltip(!showTooltip)}
                    >
                        Create a new pro forma using the same data as your base case,
                        but with the selected sensitized variables replacing their original values.
                    </Tooltip>
                </div>
            </NSCard>
        </RenderIf>
    );
};

export default SensitivityTableContainer;
